import React, { useEffect, useState } from "react";
import styles from './StudentReports.module.scss';
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import PageTitle from "../../../components/pageTitle/PageTitle";
import Dropdown from "../../../components/dropdown/Dropdown";
import axiosClient from "../../../api/axiosClient";
import CustomDatePicker from "../../../components/customDatePicker/CustomDatePicker";
import Button from "../../../components/button/Button";
import { format } from "date-fns";
import { useToasts } from "../../../contexts/ToastContext";

const StudentReports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState('ESR');
  const [programYearList, setProgramYearList] = useState([]);
  const [programYearId, setProgramYearId] = useState(null);
  const [semesterId, setSemesterId] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      await axiosClient.get(`years/details`)
      .then((response) => {
        setProgramYearList(response.data.map((item) => ({
          value: item.year.id,
          label: item.year.year,
          semesters: item.semesters
        })));
      })
      .catch((error) => { showToast(error.message, 'error'); })
    };

    fetchData();
  }, [showToast]);

  const getSemesters = () => {
    if(programYearId) {
      const selectedYearData = programYearList.find(item => item.value === parseInt(programYearId));
      if(selectedYearData?.semesters) {
        return selectedYearData.semesters.map((item) => ({
          value: item.id,
          label: item.semester
        }));
      }
    }

    return [];
  }

  const handleSubmit = async () => {
    setIsLoading(true);

    if (type === 'CLR') {
      const caseLogParams = {};
      if (fromDate) caseLogParams.startDate = format(fromDate, 'yyyy-MM-dd');
      if (toDate) caseLogParams.endDate = format(toDate, 'yyyy-MM-dd');

      await axiosClient.post('reports/case-log-report', caseLogParams, {
        responseType: 'blob',
        transformResponse: [(data) => data],
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'case_log_report.xlsx';
        link.click();
      })
      .catch((error) => {
        showToast(error.message, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
      return;
    }

    const params = {};
    if(programYearId) {
      params.year = programYearId;
    }
    if(semesterId) {
      params.semester = semesterId;
    }
    if(fromDate) {
      params.startDate = format(fromDate, 'yyyy-MM-dd');
    }
    if(toDate) {
      params.endDate = format(toDate, 'yyyy-MM-dd');
    }

    params.reportName = 'STUDENT_EVALUATION_SUMMARY';

    const link = document.createElement("a");
    link.download = "evaluation_summary.xlsx";

    await axiosClient.post('reports/evaluation-reports', params, {
      responseType: 'blob',
      transformResponse: [(data) => data],
    })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response]));
      link.href = url;
      link.click();
    })
    .catch((error) => {
      showToast(error.message, 'error');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle
          label="Reports"
        />
      </div>
      <div className={styles.container}>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className={styles.row} style={{alignItems: 'center'}}>
            <div className={styles.column20}>
              <h4 className={styles.type}>Select Report Type</h4>
            </div>
            <div className={styles.column50}>
              <Dropdown
                options={[
                  {
                    value: 'ESR',
                    label: 'Evaluation Summary Report',
                  },
                  {
                    value: 'CLR',
                    label: 'Case Log Report',
                  },
                ]}
                value={type}
                onChange={(selected) => {
                  setType(selected);
                  setProgramYearId(null);
                  setSemesterId(null);
                  setFromDate(null);
                  setToDate(null);
                }}
              />
            </div>
          </div>
          <hr />
          <div className={styles.row}>
            {type === 'ESR' && <div className={styles.column50}>
              <Dropdown
                label="Program Year"
                options={programYearList}
                value={programYearId}
                onChange={(selected) => {
                  setProgramYearId(prevValue => {
                    if(prevValue !== selected) {
                      setSemesterId(null)
                    }
                    return selected;
                  })
                }}
              />
            </div>}
            {type === 'ESR' && <div className={styles.column50}>
              <Dropdown
                label="Semester"
                options={getSemesters()}
                value={semesterId}
                onChange={(selected) => setSemesterId(selected)}
              />
            </div>}
            <div className={styles.column25}>
              <CustomDatePicker
                label="From Date"
                date={fromDate}
                onChange={(date) => setFromDate(date)}
                showIcon={true}
                maxDate={toDate || new Date()}
              />
            </div>
            <div className={styles.column25}>
              <CustomDatePicker
                label="To Date"
                date={toDate}
                onChange={(date) => setToDate(date)}
                showIcon={true}
                minDate={fromDate}
                maxDate={new Date()}
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              label="Export Report"
              variant="primary"
              size="medium"
              type="submit"
              disabled={type === 'CLR' ? false : !(programYearId || semesterId || fromDate || toDate)}
            />
          </div>
        </form>
      </div>
    </>
  );
}

export default StudentReports;
